import Axios from 'axios'
import * as Sentry from '@sentry/vue'
import { Capacitor } from '@capacitor/core'
import { HTTP } from '@awesome-cordova-plugins/http'

export default class API {
  #key
  #isAuthenticated = false

  get isAuthenticated () { return this.#isAuthenticated }

  test (instance, key) {
    const url = `${resolveInstance(instance)}/api/v2/`
    return Axios.get(url, { headers: { 'x-Auth-Key': key }, timeout: 5000 }).then(() => true).catch(err => { Sentry.captureException(err); return false })
  }

  authenticate (instance, key) {
    this.baseURL = `${resolveInstance(instance)}/api/`
    this.axios = Axios.create({ baseURL: this.baseURL, headers: { 'x-Auth-Key': key } })
    this.#isAuthenticated = true
    this.#key = key
    Sentry.setTag('instance', instance)
  }

  getEquipment (revision) {
    return this.axios.get('/v2/sync/equipment/', { params: { r: revision } }).then(res => res.data)
  }

  getActivities (revision) {
    return this.axios.get('/v2/sync/activities/', { params: { r: revision } }).then(res => res.data)
  }

  getMarkers (revision) {
    return this.axios.get('/v2/sync/markers/', { params: { r: revision } }).then(res => res.data)
  }

  getFields (revision) {
    return this.axios.get('/v2/sync/fields/', { params: { r: revision } }).then(res => res.data)
  }

  getMap (revision) {
    return this.axios.get('/v2/sync/map/', { params: { r: revision } }).then(res => res.data)
  }

  getCampaigns (revision) {
    return this.axios.get('/v2/sync/campaigns/', { params: { r: revision } }).then(res => res.data)
  }

  getRevisions (client) {
    return this.axios.get('/v2/sync/', { params: { c: client } }).then(res => res.data)
  }

  getActive (deviceID) {
    return this.axios.get(`/v2/state/${deviceID}/`).then(res => res.data)
  }

  postTraces (payload, { activeCampaign, deviceID }) {
    return this.#postBlob(`/v3/traces/${deviceID}/${activeCampaign ? `${activeCampaign}/` : ''}`, payload)
  }

  postEvents (payload, { activeCampaign, deviceID }) {
    return this.#postBlob(`/v3/events/${deviceID}/${activeCampaign ? `${activeCampaign}/` : ''}`, payload)
  }

  postMarkers (payload, { deviceID }) {
    return this.#postBlob(`/v2/markers/${deviceID}/`, payload)
  }

  postAssessments (payload, { deviceID }) {
    return this.#postBlob(`/v3/assessments/${deviceID}/`, payload)
  }

  addCampaignEquipment ({ campaignID, equipment }) {
    return this.axios.post('/v2/campaign/equipment/', { c: campaignID, e: equipment }).then(res => res.data)
  }

  getCampaignEquipment (campaignID) {
    return this.axios.get('/v3/campaign/equipment/', { params: { c: campaignID } }).then(res => res.data)
  }

  getCampaignState (campaignID, featureList, { emitterID } = {}) {
    return this.axios.get('/v3/campaign/progress/', { params: { c: campaignID, f: featureList, e: emitterID } }).then(res => res.data)
  }

  getTile ({ x, y, z }) {
    return Axios.get(`${this.baseURL}map/tiles/${z}-${x}-${y}.pbf`, { responseType: 'arraybuffer' }).then(res => res.data)
  }

  getTiles (ids) {
    return Axios
      .post(`${this.baseURL}map/tiles/chunk/`, { i: ids }, { responseType: 'arraybuffer' })
      .then(res => ({
        data: res.data,
        sizes: res.headers.get('Tile-Sizes').split(';').map(Number)
      }))
  }

  #postBlob (url, blob) {
    if (Capacitor.isNativePlatform()) {
      HTTP.setDataSerializer('raw')
      HTTP.setRequestTimeout(20)
      return HTTP
        .post(`${this.baseURL}${url}`, blob, { 'x-Auth-Key': this.#key })
        .then(res => {
          if (typeof res.data === 'string') res.data = JSON.parse(res.data)
          return res
        })
    } else {
      return this.axios.post(
        url,
        blob,
        {
          headers: { 'Content-Type': 'application/octet-stream' },
          timeout: 20000
        }
      )
    }
  }
}

const resolveInstance = instance => {
  if (instance === 'LOCALHOST') return 'http://localhost:8080'
  if (instance.match(/^\d/)) return `http://${instance}`
  if (instance === 'LOOPBACK') return 'https://loopback.krknet.de'
  const safeInstance = `${instance}`.split('/')[0].replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
  const postfix = `${instance}`.includes('/') ? `-${`${instance}`.split('/').pop()}` : ''
  return `https://tracker${postfix}.${safeInstance}.wein.services`
}
